import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
// Plain axios on purpose: the countries / PSGC lookups are third-party APIs and
// must not receive our auth token.
import axios from "axios";
import {
  useCurrentAddress,
  usePermanentAddress,
  useSaveAddress,
  useCopyCurrentToPermanent,
} from "../../../api/queries/profileQueries";
import { useCheckCompleteness } from "../../../api/queries/employeeQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { PH_POSTAL_CODES, DUPLICATE_CITIES } from "../../../utils/postalCodes";
import { fixMojibake } from "../../../utils/text";
import "bootstrap-icons/font/bootstrap-icons.css";
import { SectionToolbar, EditActions, SubHeader } from "./ProfileUI";

const EMPTY_ADDRESS = {
  addressNum: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  municipality: "",
  province: "",
  region: "",
  postalCode: "",
  country: "Philippines",
};

// Normalize a place name for comparisons (repairs "PiÃ±as"-style text too)
const sanitizeString = (str) => {
  if (!str) return "";
  return fixMojibake(str).normalize("NFC").trim();
};

// PSGC (psgc.cloud) returns ñ/Ñ garbled, e.g. "Las PiÃ±as". Repair names as
// soon as they arrive so the dropdowns show — and we save — the correct text.
const withFixedNames = (places) =>
  Array.isArray(places)
    ? places.map((p) => ({ ...p, name: fixMojibake(p.name) }))
    : [];

const EmployeeAddress = forwardRef((props, ref) => {
  const { id: employeeId } = useParams();
  // null when the employee has no address of that type yet
  const { data: current = null } = useCurrentAddress(employeeId);
  const { data: permanent = null } = usePermanentAddress(employeeId);
  const saveAddress = useSaveAddress(employeeId);
  const copyCurrentToPermanent = useCopyCurrentToPermanent(employeeId);
  const checkCompleteness = useCheckCompleteness();

  const [formData, setFormData] = useState({
    current: { ...EMPTY_ADDRESS },
    permanent: { ...EMPTY_ADDRESS },
  });

  const [editingId, setEditingId] = useState({
    current: null,
    permanent: null,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });

  const [countries, setCountries] = useState([]);
  const [geoData, setGeoData] = useState({
    regions: [],
    current: { provinces: [], cities: [], barangays: [] },
    permanent: { provinces: [], cities: [], barangays: [] },
  });

  /* ===================== API FETCHING ===================== */

  const fetchProvinces = useCallback(
    async (regionName, type) => {
      const region = geoData.regions.find(
        (r) => sanitizeString(r.name) === sanitizeString(regionName),
      );
      if (!region) return;

      try {
        if (regionName === "National Capital Region (NCR)") {
          // NCR logic: Fetch cities directly
          const res = await axios.get(
            `https://psgc.cloud/api/regions/${region.code}/cities-municipalities`,
          );
          setGeoData((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              provinces: [],
              cities: withFixedNames(res.data).sort((a, b) =>
                a.name.localeCompare(b.name),
              ),
              barangays: [],
            },
          }));
          setFormData((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              province: "National Capital Region (NCR)",
            },
          }));
        } else {
          const res = await axios.get(
            `https://psgc.cloud/api/regions/${region.code}/provinces`,
          );
          setGeoData((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              provinces: withFixedNames(res.data),
              cities: [],
              barangays: [],
            },
          }));
        }
      } catch (err) {
        console.error(err);
      }
    },
    [geoData.regions],
  );

  const fetchCities = useCallback(
    async (provinceName, type) => {
      const province = geoData[type].provinces.find(
        (p) => sanitizeString(p.name) === sanitizeString(provinceName),
      );
      if (!province) return;
      try {
        const res = await axios.get(
          `https://psgc.cloud/api/provinces/${province.code}/cities-municipalities`,
        );
        setGeoData((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            cities: withFixedNames(res.data),
            barangays: [],
          },
        }));
      } catch (err) {
        console.error(err);
      }
    },
    [geoData],
  );

  const fetchBarangays = useCallback(
    async (cityName, type) => {
      const city = geoData[type].cities.find(
        (c) => sanitizeString(c.name) === sanitizeString(cityName),
      );
      if (!city) return;
      try {
        const res = await axios.get(
          `https://psgc.cloud/api/cities-municipalities/${city.code}/barangays`,
        );
        setGeoData((prev) => ({
          ...prev,
          [type]: { ...prev[type], barangays: withFixedNames(res.data) },
        }));
      } catch (err) {
        console.error(err);
      }
    },
    [geoData],
  );

  useEffect(() => {
    if (!employeeId) return;
    // 1. Fetch Countries with explicit error handling catch blocks
    axios
      .get("https://restcountries.com/v3.1/all?fields=name")
      .then((res) => {
        const sortedCountries = res.data.map((c) => c.name.common).sort();
        setCountries(sortedCountries);
      })
      .catch((err) => {
        console.error("Countries API failed to load:", err);
        // Fallback array so your UI layout doesn't break
        setCountries(["Philippines"]);
      });

    // 2. Fetch Regions with a resilient fallback system
    axios
      .get("https://psgc.cloud/api/regions")
      .then((res) => {
        setGeoData((prev) => ({ ...prev, regions: withFixedNames(res.data) }));
      })
      .catch((err) => {
        console.error("PSGC Regions API unreachable:", err);

        setGeoData((prev) => ({ ...prev, regions: [] }));
      });
  }, [employeeId]);

  useEffect(() => {
    setFormData({
      current: current
        ? { ...EMPTY_ADDRESS, ...current }
        : { ...EMPTY_ADDRESS },
      permanent: permanent
        ? { ...EMPTY_ADDRESS, ...permanent }
        : { ...EMPTY_ADDRESS },
    });
    setEditingId({
      current: current?.id || null,
      permanent: permanent?.id || null,
    });
    if (!current?.id && !permanent?.id) setIsEditMode(false);
  }, [current, permanent]);

  useImperativeHandle(ref, () => ({
    submit: async () => await handleGlobalSave(),
  }));

  const handleChange = (e, type) => {
    const { name, value } = e.target;
    setStatusMsg({ text: "", type: "" });
    if (errors[`${type}_${name}`])
      setErrors((prev) => ({ ...prev, [`${type}_${name}`]: false }));

    setFormData((prev) => {
      let updatedSection = {
        ...prev[type],
        [name]: value,
        ...(name === "country" &&
          value !== "Philippines" && {
            region: "",
            province: "",
            city: "",
            municipality: "",
            addressLine2: "",
            postalCode: "",
          }),
        ...(name === "region" && {
          province: "",
          city: "",
          municipality: "",
          addressLine2: "",
          postalCode: "",
        }),
        ...(name === "province" && {
          city: "",
          municipality: "",
          addressLine2: "",
          postalCode: "",
        }),
        ...(name === "city" && { addressLine2: "" }),
      };

      if (name === "city") {
        const cleanCity = sanitizeString(value);
        let lookupKey = cleanCity;

        const isDupe = DUPLICATE_CITIES.some((dupe) =>
          sanitizeString(cleanCity).includes(sanitizeString(dupe)),
        );

        if (isDupe) {
          lookupKey = `${cleanCity}|${sanitizeString(updatedSection.province)}`;
        }

        updatedSection.postalCode = PH_POSTAL_CODES[lookupKey] || "";
        updatedSection.municipality = cleanCity;
      }

      return { ...prev, [type]: updatedSection };
    });

    if (name === "region") fetchProvinces(value, type);
    if (name === "province") fetchCities(value, type);
    if (name === "city") fetchBarangays(value, type);
  };

  const handleGlobalSave = async () => {
    const idToUse = employeeId;
    if (!idToUse || !isEditMode) return;
    setIsSaving(true);
    try {
      const saveTask = async (type, addressId, uuid) => {
        const {
          id,
          createdAt,
          updatedAt,
          employeeId: e1,
          ...cleanData
        } = formData[type];
        if (
          type === "permanent" &&
          !addressId &&
          !cleanData.addressLine1?.trim()
        )
          return null;
        const payload = {
          ...cleanData,
          employeeId: uuid,
          addressType: type.charAt(0).toUpperCase() + type.slice(1),
        };
        return saveAddress.mutateAsync({ id: addressId, data: payload });
      };
      await Promise.all([
        saveTask("current", editingId.current, idToUse),
        saveTask("permanent", editingId.permanent, idToUse),
      ]);
      // Recalculates Complete/Incomplete and refreshes the employee record
      await checkCompleteness.mutateAsync(employeeId);
      setStatusMsg({ text: "Saved successfully!", type: "success" });
      setIsEditMode(false);
    } catch (err) {
      setStatusMsg({
        text: getApiErrorMessage(err, "Save failed"),
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyCurrentToPermanent = () => {
    setFormData((prev) => ({
      ...prev,
      permanent: {
        ...prev.current,
        id: editingId.permanent,
        addressType: "Permanent",
      },
    }));
    copyCurrentToPermanent.mutate();
    setGeoData((prev) => ({ ...prev, permanent: { ...prev.current } }));
    setStatusMsg({ text: "Current address copied!", type: "success" });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 2000);
  };

  const handleCancel = () => {
    setFormData({
      current: current
        ? { ...EMPTY_ADDRESS, ...current }
        : { ...EMPTY_ADDRESS },
      permanent: permanent
        ? { ...EMPTY_ADDRESS, ...permanent }
        : { ...EMPTY_ADDRESS },
    });
    setIsEditMode(false);
    setErrors({});
  };

  const fieldClass = (type, name) =>
    `field-control ${!isEditMode ? "is-readonly" : ""} ${isEditMode && errors[`${type}_${name}`] ? "has-error" : ""}`;

  const renderSection = (type, title) => {
    const data = formData[type];
    const isPH = data.country === "Philippines";
    const isNCR = data.region === "National Capital Region (NCR)";
    const isMandatory = type === "current";

    return (
      <div className="h-100">
        <SubHeader
          title={title}
          icon={type === "current" ? "bi-house-door" : "bi-house-check"}
        >
          {type === "permanent" && isEditMode && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary profile-btn h-26px"
              onClick={handleCopyCurrentToPermanent}
            >
              <i className="bi bi-copy"></i> Same as current
            </button>
          )}
        </SubHeader>
        <div className="row g-3">
          <div className="col-md-12">
            <label className="profile-label">
              Country{" "}
              {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <select
              name="country"
              value={data.country || ""}
              onChange={(e) => handleChange(e, type)}
              disabled={!isEditMode}
              className={`form-select form-select-sm shadow-none ${fieldClass(type, "country")}`}
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="profile-label">
              Region{" "}
              {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH ? (
              <select
                name="region"
                value={data.region || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-select form-select-sm shadow-none ${fieldClass(type, "region")}`}
              >
                <option value="">Select Region</option>
                {geoData.regions.map((r) => (
                  <option key={r.code} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="region"
                value={data.region || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-control form-control-sm shadow-none ${fieldClass(type, "region")}`}
              />
            )}
          </div>

          <div className="col-md-6">
            <label className="profile-label">
              Province{" "}
              {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isNCR ? (
              <input
                type="text"
                readOnly
                value="National Capital Region (NCR)"
                className={`form-control form-control-sm shadow-none bg-light ${fieldClass(type, "province")}`}
              />
            ) : isPH && geoData[type].provinces.length > 0 ? (
              <select
                name="province"
                value={data.province || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-select form-select-sm shadow-none ${fieldClass(type, "province")}`}
              >
                <option value="">Select Province</option>
                {geoData[type].provinces.map((p) => (
                  <option key={p.code} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="province"
                value={data.province || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-control form-control-sm shadow-none ${fieldClass(type, "province")}`}
              />
            )}
          </div>

          <div className="col-md-6">
            <label className="profile-label">
              City / Mun.{" "}
              {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH && geoData[type].cities.length > 0 ? (
              <select
                name="city"
                value={data.city || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-select form-select-sm shadow-none ${fieldClass(type, "city")}`}
              >
                <option value="">Select City</option>
                {geoData[type].cities.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="city"
                value={data.city || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-control form-control-sm shadow-none ${fieldClass(type, "city")}`}
              />
            )}
          </div>

          <div className="col-md-6">
            <label className="profile-label">
              Barangay{" "}
              {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH && geoData[type].barangays.length > 0 ? (
              <select
                name="addressLine2"
                value={data.addressLine2 || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-select form-select-sm shadow-none ${fieldClass(type, "addressLine2")}`}
              >
                <option value="">Select Barangay</option>
                {geoData[type].barangays.map((b) => (
                  <option key={b.code} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="addressLine2"
                value={data.addressLine2 || ""}
                onChange={(e) => handleChange(e, type)}
                disabled={!isEditMode}
                className={`form-control form-control-sm shadow-none ${fieldClass(type, "addressLine2")}`}
              />
            )}
          </div>

          <div className="col-md-6">
            <label className="profile-label">Municipality</label>
            <input
              type="text"
              name="municipality"
              readOnly={isPH}
              disabled={!isEditMode}
              value={data.municipality || ""}
              onChange={(e) => handleChange(e, type)}
              className={`form-control form-control-sm shadow-none ${isPH ? "bg-light" : ""} ${fieldClass(type, "municipality")}`}
            />
          </div>
          <div className="col-md-6">
            <label className="profile-label">House/Blk/Lot</label>
            <input
              type="text"
              name="addressNum"
              disabled={!isEditMode}
              value={data.addressNum || ""}
              onChange={(e) => handleChange(e, type)}
              className={`form-control form-control-sm shadow-none ${fieldClass(type, "addressNum")}`}
            />
          </div>
          <div className="col-md-8">
            <label className="profile-label">Street/Village Name</label>
            <input
              type="text"
              name="addressLine1"
              disabled={!isEditMode}
              value={data.addressLine1 || ""}
              onChange={(e) => handleChange(e, type)}
              className={`form-control form-control-sm shadow-none ${fieldClass(type, "addressLine1")}`}
            />
          </div>
          <div className="col-md-4">
            <label className="profile-label">Postal Code</label>
            <input
              type="text"
              name="postalCode"
              disabled={!isEditMode}
              value={data.postalCode || ""}
              onChange={(e) => handleChange(e, type)}
              className={`form-control form-control-sm shadow-none ${fieldClass(type, "postalCode")}`}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="position-relative">
      <div>
        {isSaving && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}
        <SectionToolbar statusMsg={statusMsg}>
          <EditActions
            isEditing={isEditMode}
            onEdit={() => setIsEditMode(true)}
            onSave={handleGlobalSave}
            onCancel={handleCancel}
            isSaving={isSaving}
          />
        </SectionToolbar>
        <div className="row g-4">
          <div className="col-lg-6">
            {renderSection("current", "Current Address")}
          </div>
          <div className="col-lg-6">
            {renderSection("permanent", "Permanent Address")}
          </div>
        </div>
      </div>
    </div>
  );
});

export default EmployeeAddress;
